import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Search,
  ArrowRight,
  Camera,
  Image,
  Sparkles,
  Lock,
  Pill,
  ChevronDown,
  Check,
  X as XIcon,
  Plus,
  Wand2,
  PlusCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import ResultCard from "../components/ResultCard";
import SeoHead from "../components/SeoHead";
import ResultSkeleton from "../components/ResultSkeleton";
import BarcodeScanner from "../components/BarcodeScanner";
import PhotoCapture from "../components/PhotoCapture";
import PhotoOcrLimitModal from "../components/PhotoOcrLimitModal";
import PostCheckUpsell from "../components/PostCheckUpsell";
import ShareResultPrompt from "../components/ShareResultPrompt";
import RelatedChecksSection from "../components/RelatedChecksSection";
import { addRecentCheck } from "../lib/recentChecks";
import { triggerFoundingModal } from "../lib/foundingTrigger";
import { hasUsedFreeCheck, markFreeCheckUsed } from "../lib/freeCheckSession";
import SecondCheckSignIn from "../components/SecondCheckSignIn";
import {
  fetchMedications,
  fetchProducts,
  runCheck,
  getPreferences,
  savePreferences,
  aiInferMedication,
  addManualMedication,
  subscribeGapNotify,
  fetchPhotoOcrUsage,
  trackEvent,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { useAuthStatus } from "../lib/useAuthStatus";
import { cn } from "../lib/utils";

export default function Checker({ onAuthNeeded, onPaywall }) {
  const { user } = useAuth();
  const { enabled: authEnabled } = useAuthStatus();
  const [medications, setMedications] = useState([]);
  const [products, setProducts] = useState([]);
  const [medIds, setMedIds] = useState([]);
  const [productQuery, setProductQuery] = useState("");
  const [productId, setProductId] = useState("");
  const [loadingMeds, setLoadingMeds] = useState(true);
  const [running, setRunning] = useState(false);
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const [result, setResult] = useState(null);
  const [productFocused, setProductFocused] = useState(false);
  // Default the medication picker to OPEN so every visitor on every device sees
  // the same searchable list on first paint. Previously this was collapsed
  // behind a card the user had to tap, which led to inconsistent tester
  // reports — users who tapped the card got suggestions, users who didn't
  // typed their med name into the product field by mistake and thought the
  // app was broken. Opening by default removes the discoverability step.
  const [medOpen, setMedOpen] = useState(true);
  const [medFilter, setMedFilter] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoPasteOnly, setPhotoPasteOnly] = useState(false);
  const [photoLimit, setPhotoLimit] = useState(null); // null | {used, limit}
  const [photoUsage, setPhotoUsage] = useState(null); // null | {used, limit, remaining}

  // Hydrate the photo-OCR usage on mount + every time the photo dialog
  // closes, so the inline counter on the Photo button stays accurate.
  const refreshPhotoUsage = () => {
    fetchPhotoOcrUsage().then(setPhotoUsage).catch(() => {});
  };
  useEffect(() => {
    refreshPhotoUsage();
  }, []);
  const [customIngredients, setCustomIngredients] = useState(null);
  const [unregisteredScan, setUnregisteredScan] = useState(null); // {name, origin}
  const [customMeds, setCustomMeds] = useState([]); // user-added unverified meds
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiInferring, setAiInferring] = useState(false);
  // Uncovered-medication waitlist (spec B6): when a typed med isn't in the
  // catalog and isn't an ingredient, we offer a notify field instead of
  // fabricating a match.
  const [notCoveredMed, setNotCoveredMed] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notified, setNotified] = useState(false);
  const medRef = useRef(null);
  // The medication selection seeded by a ?med= deep-link (category tile / SEO
  // link), stored as a comma signature. While the live selection still equals
  // this seed we DON'T persist it — a URL-driven choice must not overwrite the
  // user's remembered meds (localStorage + server prefs, which also feed the
  // Account treatment-tracker candidates). Persisting resumes on a real edit.
  const seededSelRef = useRef(null);
  // True for the whole visit when the page was opened with a ?med= param
  // (captured on first render, before the deep-link effect clears the param).
  const seededFromUrlRef = useRef(
    typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).has("med"),
  );
  const [searchParams, setSearchParams] = useSearchParams();
  // Set true once we've consumed a ?med= + ?product= deep-link so the next
  // render auto-runs the check. Reset after firing so it never re-runs.
  const [autoRunPending, setAutoRunPending] = useState(false);

  useEffect(() => {
    // Fire the two reference-data calls independently so a flaky network on
    // the larger /api/products payload (≈400 KB JSON) doesn't take the whole
    // page down. On mobile/3G we frequently saw the products fetch time out
    // mid-transfer while medications (≈28 KB) had already arrived; the old
    // Promise.all rejected the whole block and surfaced a misleading
    // "Could not load reference data." toast even though the meds picker
    // would have worked fine.
    let medsOk = false;
    let productsOk = false;
    (async () => {
      try {
        const m = await fetchMedications();
        setMedications(m);
        medsOk = true;
      } catch (e) {
        toast.error("Couldn't load reference data", {
          description: "Check your connection and refresh — we'll have your safety checker ready in a moment.",
        });
      } finally {
        setLoadingMeds(false);
      }
    })();
    (async () => {
      try {
        const p = await fetchProducts();
        setProducts(p);
        productsOk = true;
      } catch (e) {
        // Products catalog is optional — users can still type freely or use
        // photo/barcode. Silent fail; no toast.
      }
    })();
    // Reference vars so the linter doesn't complain about unused tracking.
    void medsOk;
    void productsOk;
  }, []);

  // Phase 1.16 — hydrate saved meds on mount.
  // Logged-in: server prefs. Anonymous: localStorage fallback.
  useEffect(() => {
    // A ?med= deep-link owns the selection for this visit — don't let the
    // remembered-meds hydrate (server prefs OR localStorage) override the seed.
    if (seededFromUrlRef.current) return;
    const LS_KEY = "medskin.meds";
    if (user) {
      getPreferences()
        .then((prefs) => {
          if (Array.isArray(prefs.medication_ids) && prefs.medication_ids.length) {
            setMedIds(prefs.medication_ids.slice(0, 8));
          }
        })
        .catch(() => {}); // 401 when token expired — silent
    } else {
      try {
        const saved = JSON.parse(localStorage.getItem(LS_KEY) || "[]");
        if (Array.isArray(saved) && saved.length) {
          setMedIds(saved.slice(0, 8));
        }
      } catch (_e) { /* ignore */ }
    }
  }, [user]);

  // Persist meds whenever the user edits them (anonymous → localStorage;
  // logged-in users also get a server-side auto-save after each successful check).
  // Custom (locally-added) medication IDs are stripped — they're session-only.
  useEffect(() => {
    // During a ?med= deep-link visit, never persist an empty selection — the
    // seed resolves a render later, and writing [] first would wipe the user's
    // remembered meds (localStorage + server prefs / Account tracker candidates).
    if (seededFromUrlRef.current && medIds.length === 0) return;
    // Once the live selection equals the resolved URL seed, skip persisting
    // that URL-driven choice too. Persisting resumes on a genuine edit.
    if (seededSelRef.current !== null && medIds.join(",") === seededSelRef.current) {
      return;
    }
    const LS_KEY = "medskin.meds";
    const realIds = medIds.filter((id) => !id.startsWith("custom_"));
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(realIds));
    } catch (_e) { /* ignore */ }
    if (user && realIds.length) {
      // Best-effort server save; errors are non-blocking.
      savePreferences({ medication_ids: realIds, routine: [] }).catch(() => {});
    }
  }, [medIds, user]);

  // Auto-select a medication + product from URL params. Driven by the
  // landing-page quick-pick chips and SEO deep-links like
  //   /check?med=tretinoin&product=cerave-moisturizing-cream
  // Slug resolution priority (medications): exact id → exact normalized
  // id/generic/brand → substring on generic/brand. Products: exact id →
  // exact normalized id/name/full_name → substring on name/full_name.
  // Both params are consumed (cleared from the URL) so refresh stays clean.
  // When BOTH med + product resolve, an auto-run flag fires the check on
  // the next render so the user lands directly on the verdict card.
  useEffect(() => {
    if (!medications.length) return;
    const medSlug = (searchParams.get("med") || "").trim().toLowerCase();
    const productSlug = (searchParams.get("product") || "").trim().toLowerCase();
    if (!medSlug && !productSlug) return;
    // Products take a beat longer to hydrate; wait for them if we need to
    // resolve a product slug.
    if (productSlug && !products.length) return;

    const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");

    let resolvedMedId = null;
    if (medSlug) {
      const t = norm(medSlug);
      const exact = medications.find((m) => {
        if (m.id && m.id.toLowerCase() === medSlug) return true;
        if (norm(m.id) === t) return true;
        if (norm(m.generic_name) === t) return true;
        const brands = Array.isArray(m.brand_names) ? m.brand_names : [];
        return brands.some((b) => norm(b) === t);
      });
      const fuzzy = exact || medications.find((m) => {
        if ((m.generic_name || "").toLowerCase().includes(medSlug)) return true;
        const brands = Array.isArray(m.brand_names) ? m.brand_names : [];
        return brands.some((b) => (b || "").toLowerCase().includes(medSlug));
      });
      if (fuzzy) {
        resolvedMedId = fuzzy.id;
        // A ?med= deep-link names ONE medication to check — replace the
        // selection with just it (deterministic), and record it as the seed so
        // the persist effect won't write this URL-driven choice to the user's
        // saved preferences (which also feed the Account treatment tracker).
        seededSelRef.current = fuzzy.id;
        setMedIds([fuzzy.id]);
      }
    }

    let resolvedProduct = null;
    if (productSlug) {
      const t = norm(productSlug);
      const exact = products.find((p) => {
        if (p.id && p.id.toLowerCase() === productSlug) return true;
        if (norm(p.id) === t) return true;
        if (norm(p.full_name) === t) return true;
        if (norm(p.name) === t) return true;
        // Catalog rows can carry slug_aliases (legacy / marketing slugs that
        // should resolve to the canonical product id, e.g.
        // 'the-ordinary-retinol-1-in-squalane' → 'the-ordinary-retinol-1').
        const aliases = Array.isArray(p.slug_aliases) ? p.slug_aliases : [];
        return aliases.some((a) => a && a.toLowerCase() === productSlug);
      });
      resolvedProduct = exact || products.find((p) => {
        if ((p.full_name || "").toLowerCase().includes(productSlug)) return true;
        return (p.name || "").toLowerCase().includes(productSlug);
      });
      if (resolvedProduct) {
        setProductQuery(resolvedProduct.full_name || resolvedProduct.name || "");
        setProductId(resolvedProduct.id);
        setCustomIngredients(null);
      }
    }

    // Strip both params so a manual refresh stays clean.
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.delete("med");
      next.delete("product");
      return next;
    }, { replace: true });

    // Auto-run only when BOTH med + product resolved (a partial deep-link
    // lands the user on a pre-filled form so they can adjust before submit).
    if (resolvedMedId && resolvedProduct) {
      setAutoRunPending(true);
    }
  }, [medications, products, searchParams, setSearchParams]);

  // Click-outside + Escape for medication dropdown.
  // Note: we use ``pointerdown`` (not ``mousedown``) and a ``contains`` guard
  // PLUS a defensive ``stopPropagation`` on the popover itself (below) — some
  // browsers report scrollbar-thumb drags as a pointerdown on <html>/<body>,
  // which used to close the dropdown the moment the user tried to scroll.
  useEffect(() => {
    if (!medOpen) return undefined;
    const onPointerDown = (e) => {
      if (medRef.current && !medRef.current.contains(e.target)) {
        setMedOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMedOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [medOpen]);

  const matches = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    if (!q) return [];
    return products
      .filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.brand.toLowerCase().includes(q),
      )
      .slice(0, 5);
  }, [productQuery, products]);

  const filteredMeds = useMemo(() => {
    const q = medFilter.trim().toLowerCase();
    // Drop any rows that have neither name nor generic_name nor brand_names
    const valid = medications.filter(
      (m) => (m.name && m.name.trim()) || (m.generic_name && m.generic_name.trim())
        || (m.brand_names && m.brand_names.length > 0),
    );
    if (!q) return valid;
    return valid.filter((m) => {
      const hay = [
        m.name,
        m.generic_name,
        ...(m.brand_names || []),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [medFilter, medications]);

  const selectedMeds = useMemo(
    () => {
      const fromCatalog = medIds
        .map((id) => medications.find((m) => m.id === id))
        .filter(Boolean);
      return [...fromCatalog, ...customMeds.filter((c) => medIds.includes(c.id))];
    },
    [medIds, medications, customMeds],
  );

  const toggleMed = (id) => {
    setMedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const removeMed = (id) => {
    setMedIds((prev) => prev.filter((x) => x !== id));
    setCustomMeds((prev) => prev.filter((m) => m.id !== id));
  };

  // Trigger AI inference when filter is non-empty AND no exact catalog match.
  const showCantFind = useMemo(
    () => medFilter.trim().length >= 2 && filteredMeds.length === 0,
    [medFilter, filteredMeds.length],
  );

  // Ambiguous single-word terms that collide with the oral Vitamin A supplement
  // medication ("retinol" the ingredient vs the pill). Typed ALONE in the
  // medication box, these should redirect to the product box (they're what you
  // apply to skin), not surface the pill as the primary match.
  const AMBIGUOUS_INGREDIENT_TERMS = { retinol: "Retinol", retinyl: "Retinyl", "vitamin a": "Vitamin A" };
  const redirectTerm = AMBIGUOUS_INGREDIENT_TERMS[medFilter.trim().toLowerCase()] || null;
  const supplementMed = useMemo(
    () => medications.find(
      (m) => /retinyl palmitate|vitamin a supplement/i.test(`${m.generic_name || ""} ${m.name || ""}`),
    ) || null,
    [medications],
  );

  const runAiInfer = async () => {
    const q = medFilter.trim();
    if (!q || aiInferring) return;
    setAiInferring(true);
    setAiSuggestions([]);
    setNotCoveredMed(null);
    setNotified(false);
    try {
      const res = await aiInferMedication(q);
      if (res.best_match) {
        // High-confidence catalog match → add it.
        toggleMed(res.best_match.medication_id);
        setMedFilter("");
        toast.success(`Matched to ${res.best_match.matched_label}`, {
          description: "Added to your medication list — ready to check.",
        });
      } else if (res.ingredient_match) {
        // Typed an ingredient into the medication box → redirect it.
        toast.message("That's an ingredient, not a medication", {
          description: `Add "${res.ingredient_match.name}" in the product box below instead.`,
        });
      } else if (res.not_covered) {
        // Backend says we genuinely don't cover this (top fuzzy score < 70) →
        // offer the honest waitlist instead of a misleading loose match. The
        // coverage gap was already logged server-side for the admin queue.
        setNotCoveredMed(q);
      } else if ((res.fuzzy_matches || []).length > 0) {
        setAiSuggestions(res.fuzzy_matches);
      } else {
        setNotCoveredMed(q);
      }
    } catch (_e) {
      toast.error("Couldn't check that medication", {
        description: "Please try again in a moment.",
      });
    } finally {
      setAiInferring(false);
    }
  };

  const notifyCoverage = async () => {
    const email = notifyEmail.trim();
    if (!email) return;
    try {
      await subscribeGapNotify({ email, medication_query: notCoveredMed });
      setNotified(true);
      toast.success("You're on the list", {
        description: `We'll email you when ${notCoveredMed} is covered.`,
      });
    } catch (_e) {
      toast.error("Couldn't save that — check the email and try again.");
    }
  };

  const addManually = async () => {
    const q = medFilter.trim();
    if (!q) return;
    try {
      const m = await addManualMedication(q);
      setCustomMeds((prev) => [...prev, m]);
      setMedIds((prev) => [...prev, m.id]);
      setMedFilter("");
      setAiSuggestions([]);
      toast.success(`Added "${m.name}"`, {
        description: "Saved as a custom medication — flagged for our team to add to the main catalog.",
      });
    } catch (_e) {
      toast.error("Couldn't add that medication", {
        description: "Check your connection and try again — your other selections are safe.",
      });
    }
  };

  const checksLeft = user ? Math.max(0, user.quota - user.checks_used) : 3;

  const submit = async (overrides = {}) => {
    // When auth is enabled but the user isn't signed in, prompt sign-in.
    // When auth is disabled (free-launch phase, no Resend), the backend
    // accepts anonymous checks via session-based quota — let it through.
    // First check per session is free even when auth is enabled. The SECOND
    // check in the same session (still signed out) prompts sign-in first.
    if (!user && authEnabled && hasUsedFreeCheck()) {
      setShowSignInPrompt(true);
      return;
    }
    // Trust-building phase: every user gets a full result on every check.
    // We never open the paywall before the verdict — premium upsells live
    // BELOW the result. (Backend quota gate is also disabled.)
    const useMedIds = overrides.medication_ids || medIds;
    const useQuery = overrides.product_query ?? productQuery;
    const useProductId = overrides.product_id ?? productId;
    const useCustom = overrides.custom_ingredients ?? customIngredients;
    if (!useMedIds || useMedIds.length === 0)
      return toast.warning("Add at least one medication.", {
        description: "Pick or type a medication so we can check it against your product.",
      });
    if (!useQuery.trim() && !useProductId)
      return toast.warning("Search for a skincare product.", {
        description: "Type a product name, paste an ingredient list, or scan a barcode.",
      });

    // Custom meds (id starts with "custom_") aren't in the engine catalog.
    // Strip them before the API call, but warn the user if they were the
    // only selection.
    const realMedIds = useMedIds.filter((id) => !id.startsWith("custom_"));
    const customCount = useMedIds.length - realMedIds.length;
    if (realMedIds.length === 0) {
      return toast.warning("Add a verified medication", {
        description: "We can't run a safety check on custom-only entries yet — please add at least one from our catalog.",
      });
    }
    if (customCount > 0) {
      toast.message(
        `${customCount} custom medication${customCount === 1 ? "" : "s"} tracked for review`,
        { description: "We'll add them to the main catalog once reviewed. Verified meds will run in this check." },
      );
    }

    setRunning(true);
    setResult(null);
    // RUM: measure check-completion latency end-to-end (button click →
    // verdict rendered). Captures backend + network + render time.
    const _checkStartedAt = performance.now();
    try {
      const data = await runCheck({
        medication_ids: realMedIds,
        product_query: useQuery,
        product_id: useProductId || undefined,
        custom_ingredients: useCustom || undefined,
      });
      setResult(data);
      // Mark the free check consumed only AFTER a successful anonymous check.
      if (!user) markFreeCheckUsed();
      // Persist to anonymous localStorage history so the homepage's
      // "Your recent checks" section can offer a one-tap re-run.
      try {
        const med0 = data?.medication || (data?.medications && data.medications[0]);
        if (med0?.id) {
          addRecentCheck({
            med_id: med0.id,
            med_name: med0.name || med0.generic_name || med0.id,
            product_id: data.product_id || null,
            product_name: data.product_name || "Custom product",
            verdict: data.verdict || data.severity_tier || data.overall_result || "INSUFFICIENT_DATA",
            safety_score:
              typeof data.safety_score === "number" ? data.safety_score : null,
          });
        }
      } catch { /* localStorage blocked — silent */ }
      // Fire-and-forget RUM ping. Includes `from_share_id` when the
      // user arrived via a /r/{id} sticky-bar deep-link, so the
      // share→rerun→completion funnel can be measured.
      trackEvent("check_completion", {
        duration_ms: Math.max(0, Math.round(performance.now() - _checkStartedAt)),
        verdict:     data?.verdict || data?.overall_result,
        score:       data?.safety_score,
        from_share_id: searchParams.get("from_share_id") || undefined,
      });
      // P1 intent signal: a successful check result is now displayed — invite
      // the engaged user to the Founding offer (gated to once/session, 7-day).
      triggerFoundingModal("check_result");
      // Backend v2 doesn't echo checks_used in the body — bump locally.
      if (user && !user.is_pro) {
        user.checks_used = (user.checks_used || 0) + 1;
      }
    } catch (err) {
      // Only route to the auth modal if auth is actually live. Otherwise the
      // 401 is stale and the user would hit the "Continue as guest" dead end.
      if (err?.response?.status === 401 && authEnabled) onAuthNeeded?.();
      else if (err?.response?.status === 402) onPaywall?.();
      else {
        const detail = err?.response?.data?.detail;
        const msg = typeof detail === "string"
          ? detail
          : (detail && detail.message)
            || err?.message
            || "Could not run check.";
        toast.error(msg);
      }
    } finally {
      setRunning(false);
    }
  };

  // Deep-link auto-run: when both ?med= and ?product= resolved we set
  // ``autoRunPending``. Fire submit once medIds + productId have propagated.
  useEffect(() => {
    if (!autoRunPending) return;
    if (running) return;
    if (!medIds.length || !productId) return;
    setAutoRunPending(false);
    submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRunPending, medIds, productId]);

  return (
    <main className="bg-cream min-h-[calc(100vh-64px)] relative overflow-hidden">
      <SeoHead
        title="Compatibility checker — BlendSafe"
        description="Check whether a skincare product conflicts with your medication's FDA label. Two inputs, one result per medication, with verified alternatives when available."
        canonicalPath="/check"
      />
      <span
        aria-hidden
        className="blob"
        style={{
          width: 520, height: 520, right: -200, top: -200,
          background: "radial-gradient(closest-side, #fde4d0, transparent)",
          opacity: 0.55,
        }}
      />
      <span
        aria-hidden
        className="blob"
        style={{
          width: 360, height: 360, left: -160, bottom: 80,
          background: "radial-gradient(closest-side, #bce8c8, transparent)",
          opacity: 0.4,
        }}
      />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 md:px-8 pt-12 sm:pt-14 md:pt-16 pb-32 md:pb-20 relative">
        <div className="text-center">
          <span className="text-[11px] uppercase tracking-[0.28em] font-semibold text-ink-mute">
            Compatibility checker
          </span>
          <h1
            data-testid="checker-headline"
            className="text-[32px] sm:text-4xl md:text-5xl lg:text-6xl tracking-[-0.03em] text-ink mt-3 sm:mt-4 leading-[1.05] sm:leading-[1.02] font-extrabold scroll-mt-24 sm:scroll-mt-28"
          >
            What are you taking,{" "}
            <span className="font-editorial italic font-normal text-peach-500">
              and what are you applying?
            </span>
          </h1>
          <p className="text-[14px] sm:text-base text-ink-soft/85 mt-4 sm:mt-5 max-w-md mx-auto leading-relaxed px-2">
            Two inputs. One result. See the label section behind it, and verified
            alternatives when available.
          </p>
        </div>

        <div className="mt-7 sm:mt-10 rounded-3xl sm:rounded-[28px] bg-white/85 backdrop-blur border border-surface-line shadow-card p-3 sm:p-4 md:p-5">
          <div className="space-y-3">
            {/* Medication selector (multi-select) */}
            <div ref={medRef} className="relative w-full">
              <div
                role="button"
                tabIndex={0}
                data-testid="checker-medication-trigger"
                onClick={() => setMedOpen((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setMedOpen((v) => !v);
                  }
                }}
                className={cn(
                  "w-full min-h-16 px-4 py-3 rounded-2xl bg-cream-100 border border-surface-line",
                  "flex items-start gap-3 text-left transition-all hover:bg-cream-200 cursor-pointer",
                  "focus:outline-none focus:ring-4 focus:ring-peach-100",
                )}
              >
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-surface-line text-ink-soft mt-0.5 shrink-0">
                  <Pill className="w-4 h-4" strokeWidth={2} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[10px] uppercase tracking-[0.18em] text-ink-light font-semibold">
                    Medications {selectedMeds.length > 0 && (
                      <span className="text-peach-500">· {selectedMeds.length}</span>
                    )}
                  </span>
                  {selectedMeds.length > 0 ? (
                    <span className="mt-1.5 flex flex-wrap gap-1.5">
                      {selectedMeds.map((m) => {
                        const label =
                          m.name
                          || m.generic_name
                          || (m.brand_names && m.brand_names[0])
                          || (m.id ? `Med ${m.id.slice(0, 6)}` : "Medication");
                        return (
                          <span
                            key={m.id}
                            data-testid={`med-chip-${m.id}`}
                            className={cn(
                              "inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full border text-[12px] font-medium",
                              m.custom
                                ? "bg-amber-50 border-amber-200 text-amber-900"
                                : "bg-white border-surface-line text-ink",
                            )}
                          >
                            {label}
                            {m.custom && (
                              <span className="text-[9.5px] uppercase tracking-wide ml-0.5 px-1 rounded bg-amber-200/60">
                                custom
                              </span>
                            )}
                            <button
                              type="button"
                              data-testid={`med-chip-remove-${m.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeMed(m.id);
                              }}
                              className="ml-0.5 inline-flex w-4 h-4 items-center justify-center rounded-full text-ink-mute hover:bg-cream-100 hover:text-ink"
                              aria-label={`Remove ${label}`}
                            >
                              <XIcon className="w-3 h-3" />
                            </button>
                          </span>
                        );
                      })}
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-medium text-peach-700 bg-peach-50 border border-peach-100">
                        <Plus className="w-3 h-3" /> Add another
                      </span>
                    </span>
                  ) : (
                    <span className="block text-sm text-ink-mute font-medium mt-0.5">
                      {loadingMeds ? "Loading…" : "Add one or more medications"}
                    </span>
                  )}
                </span>
                <ChevronDown className="w-4 h-4 text-ink-light shrink-0 mt-3" />
              </div>

              {medOpen && (
                <div
                  data-testid="checker-medication-popover"
                  // Guard against scrollbar-drag closing the popover: stop
                  // pointerdown from reaching the document outside-click
                  // listener even when the browser reports <html>/<body> as
                  // the drag target. wheel + scroll never need to bubble.
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onWheel={(e) => e.stopPropagation()}
                  onScroll={(e) => e.stopPropagation()}
                  className="relative mt-2 rounded-2xl bg-white border border-surface-line shadow-card overflow-hidden"
                >
                  <div className="p-2 border-b border-surface-line">
                    <input
                      autoFocus
                      data-testid="checker-medication-search"
                      value={medFilter}
                      onChange={(e) => {
                        setMedFilter(e.target.value);
                        setAiSuggestions([]);
                      }}
                      placeholder="Search medications (e.g., Accutane, Doxycycline…)"
                      className="w-full h-10 px-3 rounded-xl bg-cream-50 text-sm text-ink placeholder:text-ink-light focus:outline-none border border-surface-line"
                    />
                  </div>
                  {redirectTerm ? (
                    <div data-testid="checker-ingredient-redirect" className="px-3 py-3 space-y-2.5">
                      <div className="rounded-xl border border-peach-200 bg-peach-50/60 p-3">
                        <p className="text-[13px] font-semibold text-ink">
                          “{redirectTerm}” is a skincare ingredient
                        </p>
                        <p className="mt-1 text-[12.5px] text-ink-soft leading-snug">
                          That's something you apply to your skin — add it in the product box
                          below instead of the medication list.
                        </p>
                        <button
                          type="button"
                          data-testid="checker-ingredient-redirect-product"
                          onClick={() => {
                            setMedFilter("");
                            setMedOpen(false);
                            setTimeout(() => {
                              const el = document.querySelector('[data-testid="checker-product-input"]');
                              if (el) {
                                el.scrollIntoView({ behavior: "smooth", block: "center" });
                                el.focus?.();
                              }
                            }, 140);
                          }}
                          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ink text-cream-50 text-[12px] font-semibold hover:bg-ink-soft transition-colors"
                        >
                          Go to product box
                        </button>
                      </div>
                      {supplementMed && (
                        <button
                          type="button"
                          data-testid="checker-ingredient-redirect-supplement"
                          onClick={() => {
                            toggleMed(supplementMed.id);
                            setMedFilter("");
                            setMedOpen(false);
                          }}
                          className="block w-full text-left text-[12px] text-ink-mute hover:text-ink underline underline-offset-2"
                        >
                          Taking an oral vitamin A supplement? Select that instead.
                        </button>
                      )}
                    </div>
                  ) : (
                  <>
                  <ul className="max-h-72 overflow-auto py-1">
                    {filteredMeds.map((m) => {
                      const checked = medIds.includes(m.id);
                      const primary =
                        m.generic_name?.trim()
                        || m.name?.trim()
                        || (m.brand_names || [])[0]
                        || "Medication";
                      const otherBrands = (m.brand_names || []).filter(
                        (b) => b && b.toLowerCase() !== primary.toLowerCase(),
                      );
                      const subtitle = otherBrands.length
                        ? `(${otherBrands.slice(0, 3).join(", ")})`
                        : (m.category || "");
                      return (
                        <li key={m.id}>
                          <button
                            type="button"
                            data-testid={`medication-option-${m.id}`}
                            onClick={() => {
                              toggleMed(m.id);
                              setMedFilter("");
                            }}
                            className={cn(
                              "w-full text-left px-4 py-3 hover:bg-cream-100 transition-colors flex items-center gap-3 border-l-2",
                              checked
                                ? "bg-peach-50/60 border-peach-400"
                                : "border-transparent",
                            )}
                          >
                            <span
                              className={cn(
                                "inline-flex w-5 h-5 items-center justify-center rounded-md border shrink-0",
                                checked
                                  ? "bg-ink border-ink text-cream-50"
                                  : "bg-white border-surface-line text-transparent",
                              )}
                              aria-hidden
                            >
                              <Check className="w-3 h-3" strokeWidth={3} />
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className="block text-[15px] font-semibold text-ink truncate">
                                {primary}
                              </span>
                              {subtitle && (
                                <span className="block text-xs text-ink-mute truncate mt-0.5">
                                  {subtitle}
                                </span>
                              )}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>

                  {/* Did you mean / Can't find footer */}
                  {showCantFind && (
                    <div
                      data-testid="checker-cantfind"
                      className="border-t border-surface-line bg-cream-50 px-3 py-3 space-y-2.5"
                    >
                      {aiSuggestions.length > 0 && (
                        <div data-testid="checker-ai-suggestions">
                          <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-ink-mute mb-1.5">
                            Did you mean?
                          </p>
                          <ul className="space-y-1">
                            {aiSuggestions.slice(0, 3).map((s) => (
                              <li key={s.medication_id}>
                                <button
                                  type="button"
                                  data-testid={`checker-ai-suggestion-${s.medication_id}`}
                                  onClick={() => {
                                    toggleMed(s.medication_id);
                                    setMedFilter("");
                                    setAiSuggestions([]);
                                  }}
                                  className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-white border border-surface-line hover:bg-cream-100"
                                >
                                  <span className="text-sm font-medium text-ink truncate">
                                    {s.matched_label}
                                    {s.generic_name && s.generic_name.toLowerCase() !== s.matched_label.toLowerCase() && (
                                      <span className="ml-1 text-ink-mute font-normal">
                                        — {s.generic_name}
                                      </span>
                                    )}
                                  </span>
                                  <span className="text-[11px] text-ink-mute shrink-0">{s.score}%</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <p className="text-[12.5px] text-ink-soft">Can't find your medication?</p>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          type="button"
                          data-testid="checker-ai-detect"
                          onClick={runAiInfer}
                          disabled={aiInferring}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ink text-cream-50 text-[12px] font-semibold hover:bg-ink-soft disabled:opacity-60 transition-colors"
                        >
                          <Wand2 className="w-3 h-3" />
                          {aiInferring ? "Checking…" : "Find match"}
                        </button>
                      </div>

                      {notCoveredMed && (
                        <div
                          data-testid="checker-med-not-covered"
                          className="mt-1 rounded-xl border border-surface-line bg-white p-3 space-y-2"
                        >
                          <p className="text-[12.5px] text-ink">
                            We don't cover <span className="font-semibold">{notCoveredMed}</span> yet.
                            {" "}Leave your email and we'll tell you the moment it's verified.
                          </p>
                          {notified ? (
                            <p data-testid="checker-med-notified" className="text-[12px] text-safe-deep font-semibold">
                              You're on the list — thanks!
                            </p>
                          ) : (
                            <div className="flex gap-2">
                              <input
                                data-testid="checker-med-notify-email"
                                value={notifyEmail}
                                onChange={(e) => setNotifyEmail(e.target.value)}
                                placeholder="you@email.com"
                                className="flex-1 h-9 px-3 rounded-lg border border-surface-line bg-cream-50 text-[13px] text-ink focus:outline-none"
                              />
                              <button
                                type="button"
                                data-testid="checker-med-notify-submit"
                                onClick={notifyCoverage}
                                disabled={!notifyEmail.trim()}
                                className="px-3 h-9 rounded-lg bg-ink text-cream-50 text-[12px] font-semibold hover:bg-ink-soft disabled:opacity-60"
                              >
                                Notify me
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  </>
                  )}

                  {medIds.length > 0 && (
                    <div className="flex items-center justify-between px-3 py-2 border-t border-surface-line bg-cream-50">
                      <span className="text-[11px] text-ink-mute">
                        {medIds.length} selected
                      </span>
                      <button
                        type="button"
                        data-testid="checker-medication-done"
                        onClick={() => setMedOpen(false)}
                        className="px-3 py-1.5 rounded-full bg-ink text-cream-50 text-[12px] font-semibold hover:bg-ink-soft transition-colors"
                      >
                        Done
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Product input.
                Mobile (<640px): Photo/Scan stack BELOW the input on their own
                row so the input keeps full width and the long placeholder
                ("Search your skincare product, e.g. CeraVe...") never clips.
                sm+ (≥640px): chips return to their original absolute-right
                position inside the input.                                  */}
            <div className="relative w-full">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-5 top-1/2 -translate-y-1/2 text-ink-light pointer-events-none" />
                <input
                  data-testid="checker-product-input"
                  value={productQuery}
                  onChange={(e) => {
                    setProductQuery(e.target.value);
                    setProductId("");
                    setCustomIngredients(null);
                  }}
                  onFocus={() => setProductFocused(true)}
                  onBlur={() => setTimeout(() => setProductFocused(false), 120)}
                  placeholder="Search your skincare product…"
                  className="w-full h-14 sm:h-16 pl-12 pr-4 sm:pr-44 rounded-2xl bg-white border border-surface-line text-[16px] sm:text-[15px] text-ink placeholder:text-ink-light focus:outline-none focus:ring-4 focus:ring-peach-100 focus:border-peach-200 transition-all"
                />
              </div>
              <div className="mt-2 sm:mt-0 flex items-center justify-end gap-1.5 sm:absolute sm:right-3 sm:top-1/2 sm:-translate-y-1/2 sm:mt-0">
                <button
                  data-testid="checker-photo-btn"
                  type="button"
                  onClick={() => {
                    // Pre-empt: if we already know the session is exhausted,
                    // skip the dialog round-trip and surface the limit modal
                    // (and the unlimited paste path) immediately.
                    if (photoUsage && photoUsage.remaining === 0) {
                      setPhotoLimit({
                        used:  photoUsage.used,
                        limit: photoUsage.limit,
                      });
                      return;
                    }
                    setPhotoOpen(true);
                  }}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all text-[12px] font-semibold",
                    photoUsage && photoUsage.remaining === 0
                      ? "bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-100"
                      : "bg-cream-100 text-ink border-surface-line hover:bg-peach-100 hover:text-peach-700",
                  )}
                  title={
                    photoUsage
                      ? `${photoUsage.remaining} of ${photoUsage.limit} free photo scans remaining`
                      : "Photo ingredient list"
                  }
                >
                  <Image className="w-3.5 h-3.5" /> Photo
                  {photoUsage && (
                    <span
                      data-testid="checker-photo-counter"
                      className={cn(
                        "ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-bold leading-none",
                        photoUsage.remaining === 0
                          ? "bg-amber-200 text-amber-900"
                          : photoUsage.remaining === 1
                            ? "bg-amber-100 text-amber-900 border border-amber-200"
                            : "bg-white text-ink-mute border border-surface-line",
                      )}
                    >
                      {photoUsage.remaining === 0
                        ? "0 left"
                        : `${photoUsage.remaining} left`}
                    </span>
                  )}
                </button>
                <button
                  data-testid="checker-barcode-btn"
                  type="button"
                  onClick={() => setScannerOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cream-100 text-ink hover:bg-peach-100 hover:text-peach-700 border border-surface-line transition-all text-[12px] font-semibold"
                  title="Scan barcode"
                >
                  <Camera className="w-3.5 h-3.5" /> Scan
                </button>
              </div>
            </div>

            {unregisteredScan && (
              <div
                data-testid="checker-unregistered-scan-banner"
                className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900 leading-snug space-y-1.5"
              >
                <div data-testid="checker-unregistered-message">
                  <span aria-hidden="true">📍 </span>
                  <span className="font-bold">{unregisteredScan.name}</span>
                  {" "}isn't in our database yet — found via barcode scan.
                  Running your check using its ingredient list.
                </div>
                {unregisteredScan.origin
                  && unregisteredScan.origin !== "en:us"
                  && unregisteredScan.origin !== "en:united-states"
                  && unregisteredScan.origin !== "unknown" && (
                  <div data-testid="checker-international-message" className="text-amber-800">
                    <span aria-hidden="true">🌍 </span>
                    International product detected ({unregisteredScan.origin}).
                    Ingredient check still works.
                  </div>
                )}
              </div>
            )}

            <Button
              data-testid="checker-submit-btn"
              onClick={submit}
              disabled={running}
              className="w-full h-16 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 text-[15px] font-semibold transition-all hover:scale-[1.005] active:scale-[0.995] disabled:opacity-70"
            >
              {running ? "Checking…" : "Check compatibility"}
              {!running && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>

          {productFocused && matches.length > 0 && productQuery && !productId && (
            <div
              data-testid="checker-product-suggestions"
              className="mt-3 rounded-2xl bg-cream-50 border border-surface-line overflow-hidden"
            >
              {matches.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  data-testid={`checker-product-suggestion-${p.id}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setProductQuery(p.name);
                    setProductId(p.id);
                    setProductFocused(false);
                  }}
                  className="w-full text-left px-5 py-3 hover:bg-cream-100 transition-colors flex items-start gap-4 border-b last:border-b-0 border-surface-line/60"
                >
                  <span className="text-[11px] uppercase tracking-[0.18em] text-ink-mute mt-1 w-28 shrink-0 font-semibold">
                    {p.brand}
                  </span>
                  <span className="text-ink font-medium flex-1">{p.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-5 flex items-center justify-between text-[12px] text-ink-mute">
          <span className="inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-peach-400" />
            {user ? (
              user.is_pro ? (
                <span data-testid="checker-quota-pro">
                  Pro
                </span>
              ) : (
                // Trust-building phase: free users get unlimited basic
                // safety checks. The "3 of 3 used" shaming label is gone;
                // premium upsells live below the result instead.
                <span data-testid="checker-quota-free">
                  Free safety checks — no limits
                </span>
              )
            ) : (
              <span data-testid="checker-quota-anon">
                {authEnabled
                  ? "Your first check is free — no sign-in needed"
                  : "Free safety checks — no sign-in required"}
              </span>
            )}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> Private by default
          </span>
        </div>

        {showSignInPrompt && !user && (
          <div className="mt-8" data-testid="checker-signin-gate">
            <SecondCheckSignIn onSent={() => { /* prompt shows its own sent state */ }} />
          </div>
        )}

        <div className="mt-12">
          {running && <ResultSkeleton />}
          {!running && result && (
            <>
              <ResultCard
                result={result}
                onPaywall={onPaywall}
                onPasteFallback={() => {
                  // Open the photo/manual modal which already exposes a paste
                  // textarea fallback. Scrolls happen inside the modal.
                  setPhotoPasteOnly(true);
                  setPhotoOpen(true);
                }}
                onTryAnotherProduct={() => {
                  setProductQuery("");
                  setProductId(null);
                  setCustomIngredients(null);
                  // Scroll to the product input so the user lands on the search field.
                  const el = document.querySelector('[data-testid="checker-product-input"]');
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                    setTimeout(() => el.focus?.(), 400);
                  }
                }}
                onRecheck={(alt) => {
                  setProductQuery(alt.name);
                  setProductId(alt.id);
                  setCustomIngredients(null);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  submit({
                    product_query: alt.name,
                    product_id: alt.id,
                    custom_ingredients: null,
                  });
                }}
              />
              {result.verdict === "SAFE_TOGETHER" && <ShareResultPrompt result={result} />}
              <RelatedChecksSection result={result} />
              {result.verdict !== "INSUFFICIENT_DATA" && <PostCheckUpsell />}
            </>
          )}
        </div>
      </div>

      <BarcodeScanner
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onMatch={(data) => {
          // Pre-fill product info from the scanned/manually-entered barcode
          const label = data.brand && data.brand !== "Unknown"
            ? `${data.brand} — ${data.name}`
            : data.name;
          setProductQuery(label);
          setProductId("");
          setCustomIngredients(data.ingredients || []);

          if (data.unregistered) {
            // Surface the inline banner instead of an error toast.
            setUnregisteredScan({
              name: data.name,
              origin: data.origin || "unknown",
            });
          } else {
            setUnregisteredScan(null);
          }

          if (!data.ingredients || data.ingredients.length === 0) {
            toast.message("Barcode scanned, but no ingredients on file", {
              description: "Try the photo scanner on the back of the box, or paste the ingredient list manually.",
            });
          }
        }}
      />
      <PhotoCapture
        open={photoOpen}
        onOpenChange={(v) => {
          setPhotoOpen(v);
          if (!v) {
            setPhotoPasteOnly(false);
            // Refresh the inline counter after every dialog close so the
            // pill reflects scans the user just used.
            refreshPhotoUsage();
          }
        }}
        pasteOnly={photoPasteOnly}
        onLimitReached={(info) => {
          setPhotoLimit(info);
          // 402 implies remaining=0 — sync the inline counter immediately.
          setPhotoUsage((u) => ({
            used:      info?.used  ?? (u?.used ?? 3),
            limit:     info?.limit ?? (u?.limit ?? 3),
            remaining: 0,
          }));
        }}
        onConfirm={({ ingredients }) => {
          // Keep whatever the user already typed as the product label;
          // only fall back to a generic placeholder when blank.
          if (!productQuery.trim()) {
            setProductQuery("Custom ingredient list (photo)");
          }
          setProductId("");
          setCustomIngredients(ingredients);
          toast.success(`Read ${ingredients.length} ingredient${ingredients.length === 1 ? "" : "s"} from your photo`, {
            description: "Review the list below — tap any ingredient to remove it before checking.",
          });
        }}
      />
      <PhotoOcrLimitModal
        open={!!photoLimit}
        onOpenChange={(v) => { if (!v) setPhotoLimit(null); }}
        used={photoLimit?.used ?? 3}
        limit={photoLimit?.limit ?? 3}
        authEnabled={authEnabled}
        onCreateAccount={() => {
          setPhotoLimit(null);
          if (authEnabled) {
            onAuthNeeded?.();
          } else {
            toast.message("Free accounts launch soon", {
              description: "Until then, paste your ingredient list — it's unlimited.",
            });
          }
        }}
        onPasteInstead={() => {
          setPhotoLimit(null);
          setPhotoPasteOnly(true);
          setPhotoOpen(true);
        }}
      />
    </main>
  );
}
